import type { LoggerEvent } from '@/types/logger.types'
import { timer } from '@/utils/helpers'
import { loadTurnstile } from '@/utils/load-turnstile'

import { AdConsoleLogger } from './utils/ad-console-logger'

const adConsoleLogger = new AdConsoleLogger('detector')

const FRAME_TIMEOUT_MS = 15000

export type BotStatus = 'human' | 'bot' | 'unknown'

// Turnstile error families 300xxx and 600xxx mean "challenge failed": the visitor looked automated.
// Every other code (network, unsupported browser, sitekey/hostname misconfiguration) says nothing about the visitor.
const isChallengeFailure = (errorCode: string) => /^[36]/.test(errorCode)

// Resolves with '' when the challenge passed, otherwise with the Turnstile error code or the failure kind.
async function challengeInPage(siteKey: string): Promise<string> {
    const turnstile = await loadTurnstile()
    if (!turnstile) {
        return 'script_error'
    }

    const container = document.createElement('div')
    document.body.appendChild(container)
    const { promise, resolve } = Promise.withResolvers<string>()
    try {
        turnstile.render(container, {
            sitekey: siteKey,
            action: 'ads',
            retry: 'never',
            callback: () => resolve(''),
            'error-callback': resolve,
            'unsupported-callback': () => resolve('unsupported'),
        })
    } catch (error) {
        adConsoleLogger.error('Turnstile render failed', error)
        resolve('render_error')
    }

    const detail = await promise
    turnstile.remove(container)
    container.remove()
    return detail
}

async function challengeInFrame(frameOrigin: string, siteKey: string): Promise<string> {
    const frame = document.createElement('iframe')
    frame.src = `${frameOrigin}/ads-api/bot-check?sitekey=${encodeURIComponent(siteKey)}`
    frame.setAttribute('aria-hidden', 'true')
    frame.style.cssText = 'position:absolute;width:0;height:0;border:0'
    const { promise, resolve } = Promise.withResolvers<string>()
    const onMessage = (event: MessageEvent) => {
        if (event.origin === frameOrigin && event.source === frame.contentWindow && typeof event.data === 'string') {
            resolve(event.data)
        }
    }
    window.addEventListener('message', onMessage)
    document.body.appendChild(frame)
    timer(FRAME_TIMEOUT_MS).then(() => resolve('frame_timeout'))

    const detail = await promise
    window.removeEventListener('message', onMessage)
    frame.remove()
    return detail
}

export async function detectBot(
    siteKey: string,
    logEvent: (event: LoggerEvent) => void,
    frameOrigin?: string,
): Promise<BotStatus> {
    const detail = frameOrigin ? await challengeInFrame(frameOrigin, siteKey) : await challengeInPage(siteKey)
    let status: BotStatus = 'unknown'
    if (detail === '') {
        status = 'human'
    } else if (isChallengeFailure(detail)) {
        status = 'bot'
    }
    adConsoleLogger.debug('bot status', { status, detail })
    logEvent({ event: 'custom_event', eventName: 'ad_bot_detect_attempt', dimension1: status, dimension2: detail })
    return status
}
